import './copy-text.scss';

import { PropsWithChildren, useCallback, useEffect, useState } from 'react';

import { copyToClipboardHandler } from '../../../data/utils/copy-to-clipboard-handler';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { CustomFormattedMessage } from '../../intl/custom-formatted-message';

type CopyTextProps = PropsWithChildren<{
  className?: string;
  text: string;
  message?: JSX.Element | string;
  onCopy?: () => void;
}>;

export function CopyText({
  className,
  text,
  message,
  onCopy,
  children,
}: CopyTextProps) {
  const [showCallback, setShowCallback] = useState<boolean>(false);

  const onHandleCopy = useCallback(async () => {
    const result = await copyToClipboardHandler(text);

    if (result) {
      setShowCallback(true);

      setTimeout(() => {
        setShowCallback(false);
      }, 2000);

      onCopy?.();
    }
  }, [text, onCopy]);

  const onHideCallback = useCallback(() => {
    setShowCallback(false);
  }, []);

  useEffect(() => {
    document.addEventListener('click', onHideCallback);

    return () => {
      document.removeEventListener('click', onHideCallback);
    };
  }, [onHideCallback]);

  return (
    <span
      className={`copy-text${className ? ` copy-text--${className}` : ''}${
        showCallback ? ' active' : ''
      }`}
      role="button"
      tabIndex={0}
      onClick={onHandleCopy}
      onKeyDown={makeKeyDownHandler(onHandleCopy)}
    >
      <span className="copy-text__content">{children}</span>
      {showCallback && (
        <span className="copy-text-message">
          {message ?? (
            <CustomFormattedMessage id="common.copied-to-clipboard" />
          )}
        </span>
      )}
    </span>
  );
}
